import { useForgotPassword } from "../../queries/auth";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import AuthLayout from "../../components/component/AuthLayout";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

interface ForgotPasswordFormValues {
  email: string;
}

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
});

export default function ForgotPasswordPage() {
  const { mutate: forgotPassword, isPending } = useForgotPassword();

  const initialValues: ForgotPasswordFormValues = {
    email: "",
  };

  const handleSubmit = (
    values: ForgotPasswordFormValues,
    { setSubmitting, resetForm }: any,
  ) => {
    forgotPassword(values.email, {
      onSuccess: (data) => {
        toast("Email sent for the registered mail", {
          type: "success",
          autoClose: 2000,
        });
        resetForm();
      },
      onError: (error: any) => {
        toast(
          error?.response?.data?.errorMessage || "Failed to send reset email",
          {
            type: "error",
            autoClose: 2000,
          },
        );
      },
      onSettled: () => {
        setSubmitting(false);
      },
    });
  };

  return (
    <AuthLayout>
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold mb-1">
          Reset Password
        </h1>
        <p className="text-gray-500 mb-8">
          Enter your email to receive a password reset link
        </p>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-6">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Email
                </label>
                <Field
                  id="email"
                  name="email"
                  type="email"
                  className="w-full px-4 py-2.5 md:py-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Enter your email address"
                />
                <ErrorMessage
                  name="email"
                  component="p"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-primary text-white font-medium py-3 px-4 rounded-md hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting || isPending}
              >
                {isSubmitting || isPending ? "Sending..." : "Send Reset Link"}
              </button>

              <Link
                to="/login"
                className="flex items-center justify-center text-sm text-blue-500 hover:underline"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-1"
                >
                  <path d="m12 19-7-7 7-7" />
                  <path d="M19 12H5" />
                </svg>
                Back to Login
              </Link>
            </Form>
          )}
        </Formik>
      </div>
    </AuthLayout>
  );
}
